import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AUTH_EVENT, getActiveSession } from "../utils/nightStayAccount";
import { clearCart, fetchRemoteCart, upsertRemoteCartItem, selectCartItems } from "../store/cartSlice";
import {
  buildAffiliateAttributionKeyPart,
  extractAffiliateAttributionFromItem,
  getEffectiveAffiliateAttribution,
  mergeAffiliateAttributionIntoMetadata,
  mergeAffiliateAttributionIntoPayload,
} from "../utils/affiliateAttribution";

const buildGuestMixKey = (guestMix) => {
  if (!guestMix) return "guest:none";
  const adult = Number(guestMix.adult ?? guestMix.adults ?? guestMix.adultCount ?? 0);
  const child = Number(guestMix.child ?? guestMix.children ?? guestMix.childCount ?? 0);
  const infant = Number(guestMix.infant ?? guestMix.infants ?? guestMix.infantCount ?? 0);
  return `guest:A${Number.isFinite(adult) ? adult : 0}C${Number.isFinite(child) ? child : 0}I${Number.isFinite(infant) ? infant : 0}`;
};

const normalizeCouponCode = (value) => String(value || "").trim().toUpperCase();

const normalizeConfigList = (items = []) =>
  (items || [])
    .map((item) => ({
      id: item?.id || item?.key || item?.label || "",
      quantity: Math.max(1, Number(item?.quantity || item?.qty || item?.count || 1)),
    }))
    .filter((item) => item.id)
    .sort((a, b) => a.id.localeCompare(b.id))
    .map((item) => `${item.id}:${item.quantity}`)
    .join(",");

const buildCartKey = ({
  experienceType,
  identifier,
  date,
  slot,
  vehicle,
  entryPoint,
  guestMix,
  addOns,
  selectedCharges,
  checkOut,
  ratePlanId,
  couponCode,
  affiliateCode,
  campaignCode,
  linkCode,
}) => {
  const referralContext = getEffectiveAffiliateAttribution({
    affiliateCode,
    campaignCode,
    linkCode,
    couponCode,
  });
  const addOnKey = normalizeConfigList(addOns);
  const chargeKey = normalizeConfigList(selectedCharges);
  const normalizedCoupon = normalizeCouponCode(referralContext?.couponCode);
  const couponKey = normalizedCoupon ? `coupon:${normalizedCoupon}` : "";
  const referralKey = buildAffiliateAttributionKeyPart(referralContext);
  if ((experienceType || "safari") === "room") {
    return [
      "room",
      identifier || "xp",
      date || "na",
      checkOut || "na",
      ratePlanId || "rate",
      `addons:${addOnKey || "none"}`,
      `charges:${chargeKey || "none"}`,
      ...(referralKey ? [referralKey] : []),
      ...(couponKey ? [couponKey] : []),
      buildGuestMixKey(guestMix),
    ]
      .map((p) => String(p || "").trim())
      .join("#");
  }
  const slotId = slot?.id || slot?.key || slot?._id || slot?.label || "slot";
  const vehicleId = vehicle?.id || vehicle?.key || vehicle?._id || vehicle?.label || "vehicle";
  const parts = [
    experienceType || "safari",
    identifier || "xp",
    date || "na",
    slotId,
    vehicleId,
    entryPoint || "",
    `addons:${addOnKey || "none"}`,
    `charges:${chargeKey || "none"}`,
    ...(referralKey ? [referralKey] : []),
    ...(couponKey ? [couponKey] : []),
    buildGuestMixKey(guestMix),
  ];
  return parts.map((p) => String(p || "").trim()).join("#");
};

const mapItemToServerPayload = (item = {}) => {
  const referralContext = extractAffiliateAttributionFromItem(item);
  const options = item.options || {};
  const slot = options.slot || item.slot || null;
  const vehicle = options.vehicle || item.vehicle || null;
  const addOns = options.addOns || item.addOns || [];
  const selectedCharges = options.selectedCharges || item.selectedCharges || [];
  const requiredCharges = options.requiredCharges || item.requiredCharges || [];
  const guestMix = options.guestMix || item.guestMix || null;
  const totals = options.totals || item.pricingSnapshot || item.quote?.totals || null;
  const couponCode =
    referralContext?.couponCode ||
    options.couponCode ||
    item.couponCode ||
    item.metadata?.couponCode ||
    "";
  const checkIn = options.checkIn || options.date || item.checkIn || item.travelDate || item.date || null;
  const checkOut = options.checkOut || item.checkOut || item.metadata?.checkOut || item.metadata?.checkout || null;
  const ratePlanId =
    options.ratePlanId ||
    options.ratePlan?.id ||
    item.ratePlanId ||
    item.metadata?.ratePlanId ||
    item.metadata?.ratePlan ||
    "";

  const payload = {
    experienceType: item.experienceType || item.type || "safari",
    identifier: item.identifier || item.id,
    title: item.title || item.vehicle?.label || "Experience",
    travelDate: checkIn,
    entryPoint: options.entryPoint || item.entryPoint || "",
    slot,
    vehicle,
    quantity: item.quantity || 1,
    guestMix,
    addOns,
    selectedCharges,
    requiredCharges,
    notes: options.notes || item.notes || "",
    couponCode,
    pricingSnapshot: totals,
    policiesSnapshot: item.policiesSnapshot || options.policiesSnapshot || null,
    quoteMeta: item.quoteMeta || options.quoteMeta || null,
    guardrailVersion: options.guardrailVersion || item.guardrailVersion || "",
    destination: item.destination || (item.subtitle ? { title: item.subtitle } : null),
    quoteCode: item.quoteCode || item.quote?.quoteCode || "",
    metadata: mergeAffiliateAttributionIntoMetadata(
      {
        ...(item.metadata || {}),
        meta: item.meta,
        tag: item.tag,
        kind: item.kind,
        checkIn,
        checkOut,
        ratePlanId,
        couponCode,
      },
      referralContext,
    ),
    checkOut,
    ratePlanId,
  };

  payload.key =
    item.serverId ||
    buildCartKey({
      experienceType: payload.experienceType,
      identifier: payload.identifier,
      date: payload.travelDate,
      checkOut,
      ratePlanId,
      slot,
      vehicle,
      entryPoint: payload.entryPoint,
      guestMix: payload.guestMix,
      addOns: payload.addOns,
      selectedCharges: payload.selectedCharges,
      couponCode,
      affiliateCode: referralContext?.affiliateCode || "",
      campaignCode: referralContext?.campaignCode || "",
      linkCode: referralContext?.linkCode || "",
    });

  return mergeAffiliateAttributionIntoPayload(payload, referralContext);
};

function CartBootstrapper({ conciergeCartEnabled = process.env.REACT_APP_AI_CONCIERGE_CART_DRAWER_ENABLED === "true" } = {}) {
  const dispatch = useDispatch();
  const cartItems = useSelector(selectCartItems);
  const lastSessionKey = useRef("");
  const cartItemsRef = useRef([]);
  const syncingRef = useRef(false);

  useEffect(() => {
    cartItemsRef.current = cartItems || [];
  }, [cartItems]);

  useEffect(() => {
    if (!conciergeCartEnabled) return undefined;
    const readConfirmedCart = (event) => {
      if (event?.detail?.source !== "ai-concierge-configured-cart" || !getActiveSession()) return;
      // Read the persisted cart for the badge and normal cart view. Never refresh
      // quotes or upload local items as a side effect of this notification.
      dispatch(fetchRemoteCart({ readOnly: true }));
    };
    window.addEventListener("ecotour:cart-updated", readConfirmedCart);
    return () => window.removeEventListener("ecotour:cart-updated", readConfirmedCart);
  }, [conciergeCartEnabled, dispatch]);

  useEffect(() => {
    const syncCart = async () => {
      const session = getActiveSession();
      const key = session?.email || session?.phone || session?._id || "";
      if (!key) {
        lastSessionKey.current = "";
        dispatch(clearCart());
        return;
      }
      const pendingLocal = (cartItemsRef.current || []).filter((item) => !item.serverId && (item.identifier || item.id));
      if (lastSessionKey.current === key && !pendingLocal.length) return;
      lastSessionKey.current = key;
      if (syncingRef.current) return;
      syncingRef.current = true;
      if (pendingLocal.length) {
        for (const item of pendingLocal) {
          try {
            await dispatch(upsertRemoteCartItem(mapItemToServerPayload(item))).unwrap();
          } catch (_) {
            // ignore failures; user may be guest or payload invalid
          }
        }
      }
      try {
        await dispatch(fetchRemoteCart()).unwrap();
      } finally {
        syncingRef.current = false;
      }
    };

    syncCart();
    if (typeof window !== "undefined") {
      window.addEventListener(AUTH_EVENT, syncCart);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener(AUTH_EVENT, syncCart);
      }
    };
  }, [dispatch]);

  return null;
}

export default CartBootstrapper;
